/**
 * WizardChrome — the shared brand language for every Avium wizard
 * (Doc → Stories, spreadsheet import, Jira/Azure setup, first-run setup).
 *
 * Two pieces:
 *  - WizardBand: the dark-teal gradient band with the amber ◆ eyebrow — the
 *    same banner language as PageChrome, for wizards hosted OUTSIDE a page
 *    banner (modals, setup flows).
 *  - StepHeading: the in-step heading for wizards already inside a PageChrome
 *    banner — amber ◆ eyebrow (light-bg variant) + title + sub, giving each
 *    step the brand hierarchy without stacking a second full band.
 *
 * Extracted per the "two consumers → shared component" rule when the wizard
 * branding sweep (2026-06-11) reached its second wizard.
 */

import { STAGE_LABEL_OPTIONS, stageKeyForLabel, stageLabel } from '../../lib/stageVocabulary';
import type { ImportPreview } from '../../types';

// The ImportPreview shape now lives in the shared types home (AVM-221 item 6);
// re-exported here so existing wizard imports keep resolving.
export type { ImportPreview } from '../../types';
// The label↔key vocabulary now lives in lib/stageVocabulary (AVM-221 item 3);
// re-exported for the setup pages that import it from here today.
export { stageKeyForLabel } from '../../lib/stageVocabulary';

export function WizardBand({ eyebrow, title, sub, right }: {
  eyebrow: string;
  title: string;
  sub?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-gradient-to-r from-brand-primary-dark to-brand-primary px-5 py-4 mb-5 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-amber-300 text-[11px] font-black tracking-normal flex items-center gap-1.5">
          <span>◆</span> {eyebrow}
        </div>
        <h2 className="text-white text-xl font-bold mt-1 leading-tight">{title}</h2>
        {sub && <p className="text-teal-100/90 text-xs mt-1 leading-snug max-w-2xl">{sub}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

export interface WizardStepDef<T extends string> {
  id: T;
  label: string;
  /** One-line meaning of the step — what happens here. */
  hint?: string;
}

/**
 * The branded step workflow every multi-step wizard shares (Doc → Stories,
 * spreadsheet import, Jira setup). Big, meaningful, and unambiguous about
 * where you are: done = filled teal ✓, the CURRENT stage = amber/spark with
 * a "◆ you are here" caption, pending = slate. `completed` renders every
 * step done (the results/done screen).
 */
export function WizardSteps<T extends string>({ current, steps, completed }: {
  current: T;
  steps: Array<WizardStepDef<T>>;
  completed?: boolean;
}) {
  const currentIdx = completed ? steps.length : steps.findIndex(s => s.id === current);
  return (
    <div className="flex items-start gap-0 mb-8">
      {steps.map((step, i) => {
        const done = i < currentIdx;
        const active = !completed && i === currentIdx;
        return (
          <div key={step.id} className="flex items-start flex-1 min-w-0">
            <div className="flex items-start gap-2.5 flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base font-black border-2 transition-all ${
                done   ? 'bg-brand-primary border-brand-primary dark:border-brand-accent text-white' :
                active ? 'bg-brand-spark border-brand-spark text-white ring-4 ring-orange-200 shadow-md' :
                         'bg-card border-line text-ink-faint'
              }`}>
                {done ? '✓' : i + 1}
              </div>
              <div className="hidden sm:block pt-0.5">
                <div className={`text-sm leading-tight ${
                  active ? 'font-black text-brand-spark-dark' : done ? 'font-bold text-brand-primary dark:text-brand-accent' : 'font-bold text-ink-faint'
                }`}>
                  {step.label}
                </div>
                {step.hint && (
                  <div className={`text-[11px] mt-0.5 leading-snug hidden md:block ${active ? 'text-ink-muted' : 'text-ink-faint'}`}>
                    {step.hint}
                  </div>
                )}
                {active && (
                  <div className="text-[9px] font-black tracking-normal text-brand-spark mt-0.5">
                    ◆ You are here
                  </div>
                )}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-1 rounded mx-3 mt-4 ${done ? 'bg-brand-primary' : 'bg-line'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Unified setup wizard vocabulary (AVM-196 Phase B.0, mock-approved) ──────
// The canonical five steps every SOURCE SETUP flow speaks — one shell, one
// language, whether it's Jira, Azure DevOps, Asana, or (visually) the file
// wizard. Ids are per-flow (each page keeps its own step machinery); labels
// and hints are shared so the story never drifts between sources.

export const SETUP_STEP_LANGUAGE = {
  connect: { label: 'Connect', hint: 'prove Avium can see it' },
  scope: { label: 'Scope', hint: 'choose what comes in' },
  map: { label: 'Map', hint: 'review what Avium proposes' },
  preview: { label: 'Preview', hint: 'see it before it lands' },
  sync: { label: 'First sync', hint: 'and you’re live' },
} as const;

export function setupSteps<T extends string>(ids: Array<[T, keyof typeof SETUP_STEP_LANGUAGE]>): Array<WizardStepDef<T>> {
  return ids.map(([id, key]) => ({ id, label: SETUP_STEP_LANGUAGE[key].label, hint: SETUP_STEP_LANGUAGE[key].hint }));
}

export interface MappingReviewRow {
  /** The source tool's own word — shown verbatim, never rewritten. */
  native: string;
  /** How Avium will read it (the delivery phase / stage). */
  reads: string;
  /** true = confidently matched; false = worth a human glance. */
  matched: boolean;
  /** The admin explicitly changed this row (Phase B.0-iii) — it will be pinned on save. */
  edited?: boolean;
}

/** The Map step's ordered stage options — canonical labels from the vocabulary. */
export const MAP_STAGE_OPTIONS = STAGE_LABEL_OPTIONS;

/**
 * The Map step's proposal table (Phase B.0 mock treatment): the source's own
 * vocabulary → how Avium reads it, with matched / check-this-one chips. The
 * native column is display-only by design — Avium layers its reading on top
 * and never rewrites a source's categories.
 */
export function MappingReviewTable({ nativeNoun, rows, onEdit }: {
  nativeNoun: string;
  rows: MappingReviewRow[];
  /** When provided (Phase B.0-iii), each row's reading becomes adjustable — an
   *  explicit change is a deliberate decision: it pins that status's stage
   *  against future source re-derivation, audited like any Settings edit. */
  onEdit?: (native: string, readsLabel: string) => void;
}) {
  return (
    <div className="rounded-xl border border-line overflow-x-auto">
      <table className="w-full text-sm min-w-[440px]">
        <thead>
          <tr className="bg-wash border-b border-line text-left text-xs text-ink-faint uppercase tracking-wide">
            <th className="px-4 py-2 font-semibold">Your {nativeNoun}</th>
            <th className="px-2 py-2" />
            <th className="px-4 py-2 font-semibold">Avium reads it as</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line-soft">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-4 py-2.5 font-semibold text-ink">{r.native}</td>
              <td className="px-2 py-2.5 text-ink-faint">→</td>
              <td className="px-4 py-2.5">
                {onEdit ? (
                  <select
                    aria-label={`Stage for ${r.native}`}
                    value={r.reads}
                    onChange={e => onEdit(r.native, e.target.value)}
                    className="text-xs font-bold border border-line rounded-lg px-2 py-1 bg-card text-brand-primary dark:text-brand-accent"
                  >
                    {MAP_STAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-brand-primary-light dark:bg-brand-primary/25 text-brand-primary dark:text-brand-accent border border-line px-2.5 py-0.5 text-xs font-bold">{r.reads}</span>
                )}
              </td>
              <td className="px-4 py-2.5">
                {r.edited ? (
                  <span className="text-[11px] font-bold rounded-full bg-brand-primary-light dark:bg-brand-primary/25 text-brand-primary dark:text-brand-accent px-2 py-0.5 whitespace-nowrap">your choice — will be pinned</span>
                ) : r.matched ? (
                  <span className="text-[11px] font-bold rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 whitespace-nowrap">matched</span>
                ) : (
                  <span className="text-[11px] font-bold rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 whitespace-nowrap">check this one</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The Map step's trust footer — the never-overwrite promise, stated where the user decides. */
export function SourceOfTruthCallout({ sourceLabel, nativeNoun }: { sourceLabel: string; nativeNoun: string }) {
  return (
    <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-line bg-brand-primary-light/30 dark:bg-brand-primary/15 px-4 py-3 text-sm text-ink-muted">
      <span aria-hidden>🔒</span>
      <div>
        <strong className="text-ink">{sourceLabel} stays the source of truth.</strong>{' '}
        Avium layers its reading on top of your native {nativeNoun} — it never renames, remaps, or writes back your categories.
      </div>
    </div>
  );
}

// ── Import preview panel (Phase B.0-ii) ─────────────────────────────────────
// "Here's exactly what will land": a small sample of real rows read through
// the connection's own scope, ALWAYS disclosed against the truth (RULE 1) —
// exact/approximate totals when the source can answer, or an honest "totals
// appear with the first sync" when it can't. Shared by all three connectors.

export function ImportPreviewPanel({ preview, loading, approxTotal }: {
  preview: ImportPreview | null;
  loading: boolean;
  /** true when the total is approximate (Jira) — rendered with ≈. */
  approxTotal?: boolean;
}) {
  if (loading) return <div className="text-sm text-ink-faint py-3">Reading a sample from the source…</div>;
  if (!preview) return null; // fetch failed — the step's summary card still stands; never fake a sample
  const totalLine = preview.total !== null
    ? `Showing the first ${preview.sampleSize} of ${approxTotal ? '≈' : ''}${preview.total.toLocaleString()} that will land`
    : `Showing the first ${preview.sampleSize} — totals appear with the first sync`;
  return (
    <div className="mt-4">
      <div className="rounded-xl border border-line overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="bg-wash border-b border-line text-left text-xs text-ink-faint uppercase tracking-wide">
              <th className="px-4 py-2 font-semibold">Key</th>
              <th className="px-4 py-2 font-semibold">Title</th>
              <th className="px-4 py-2 font-semibold">Status → reads as</th>
              <th className="px-4 py-2 font-semibold">Assignee</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {preview.sample.map(r => (
              <tr key={r.key}>
                <td className="px-4 py-2 font-mono font-bold text-ink-muted whitespace-nowrap">{r.key}</td>
                <td className="px-4 py-2 text-ink max-w-md truncate" title={r.title}>{r.title}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="text-ink-muted">{r.status}</span>
                  {r.stage && <span className="ml-2 inline-flex items-center rounded-full bg-brand-primary-light dark:bg-brand-primary/25 text-brand-primary dark:text-brand-accent border border-line px-2 py-0.5 text-xs font-bold">{stageLabel(r.stage)}</span>}
                </td>
                <td className="px-4 py-2 text-ink-faint whitespace-nowrap">{r.assignee ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-faint mt-2">
        {totalLine} · {preview.peopleInSample} {preview.peopleInSample === 1 ? 'person' : 'people'} in this sample
      </p>
    </div>
  );
}

export function StepHeading({ eyebrow, title, sub }: {
  eyebrow: string;
  title: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="text-brand-spark-dark text-[10px] font-black tracking-normal flex items-center gap-1.5 mb-0.5">
        <span>◆</span> {eyebrow}
      </div>
      <h2 className="text-xl font-bold text-ink leading-tight">{title}</h2>
      {sub && <p className="text-sm text-ink-faint mt-1">{sub}</p>}
    </div>
  );
}
